/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useState } from 'react';
import { makeStyles, Box, Typography, Theme, IconButton, Divider } from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import VideoPlayer from '../../components/VideoPlayer';
import Placeholder, { PlaceholderType } from '../../components/Placeholder';
import VideoPlayerModal from '../../components/VideoPlayerModal';
import { EntrepreneurProfileBody } from '../../types/types';
import { StyledText } from '../../components/StyledText';


export enum PitchPanelItem {
  None = 0,
  Elevator = 1,
  FullPitch = 2,
  LinkedIn = 3,
  ExecutiveSummary = 4,
  PitchDeck = 5,
  General = 6
}

const useStyles = makeStyles<Theme, FileCardStyleProps>((theme: Theme) => ({
  root: {
    maxWidth: 928
  },
  elevator: {
    minWidth: 0,
    maxWidth: 928,
    height: 450,
    position: 'relative'
  },
  docs: {
    display: 'flex',
    flexWrap: 'wrap',
    padding: '0 20px 0 20px',
    [theme.breakpoints.up('md')]: {
      padding: '0 35px 0 35px'
    },
    justifyContent: 'flex-start',
    '& img': {
      objectFit: 'contain'
    }
  },
  featuredPlaceholderContainer: {
    height: '450px',
    maxHeight: '100%',
    marginLeft: '0',
    marginRight: '0',
    marginBottom: '30px',
    position: 'relative',

    '&:hover': {
      cursor: (props) => (props.addable ? 'pointer' : 'arrow')
    },
    '&:hover #placeholder': {
      opacity: (props) => (props.addable ? 0.5 : 1.0)
    },

    '&:hover #addWrapper': {
      opacity: (props) => (props.addable ? 1.0 : 0.0),
      border: `5px solid ${theme.palette.secondary.main}`,
      clickEvents: (props) => (props.addable ? 'auto' : 'none')
    },

    '&:hover #addWrapper button': {
      opacity: (props) => (props.addable ? 1.0 : 0.0)
    }
  },
  addWrapper: {
    width: '100%',
    height: 'inherit',
    position: 'absolute',
    justifyContent: 'center',
    alignItems: 'center',
    display: (props) => (props.addable ? 'flex' : 'none'),
    top: 0,
    left: 0,
    pointerEvents: (props) => (props.addable ? 'auto' : 'none'),
    opacity: 0,
    zIndex: 100,
    '& .MuiSvgIcon-fontSizeLarge': {
      fontSize: '3.8rem'
    }
  },

  editIconWrapper: {
    position: 'absolute',
    right: '0px',
    top: '0px',
    zIndex: 1,
    backgroundColor: '#fff',
    borderRadius: '50%',
    boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.25)',
    display: (props) => (props.editable ? 'inline' : 'none'),
    opacity: (props) => (props.editable ? 1 : 0),
    clickEvents: (props) => (props.editable ? 'auto' : 'none'),

    '& #editButton': {
      zIndex: 1000
    },

    '&:hover #editIcon': {
      color: theme.palette.secondary.dark
    }
  }
}));

const getFeaturedVidUrl = (primary: string|null|undefined, backup: string|null|undefined) => {
  if (primary && primary.trim() !== '') {
    return primary;
  }
  if (backup && backup.trim() !== '') {
    return backup;
  }
  return null;
};

const getSecondaryVidUrl = (primary: string|null|undefined, backup: string|null|undefined) => {
  if (primary && primary.trim() !== '' && backup && backup.trim() !== '') {
    return backup;
  }
  return null;
};

interface EntrepreneurPitchPanelProps {
  profile: EntrepreneurProfileBody
  handleEditItem?: (item :PitchPanelItem) => void
}

interface FileCardStyleProps {
  editable: boolean,
  addable: boolean
}

const useFileCardStyles = makeStyles<Theme, FileCardStyleProps>((theme: Theme) => ({
  fileCard: {
    textDecoration: 'none',
    margin: 0,
    padding: 10,
    flex: '1 1 25%',
    minWidth: 189,
    '&:hover': {
      cursor: 'pointer'
    }
  },
  fileCardContent: {
    display: 'flex',
    alignItems: 'center',
    height: 107,
    border: '.001px solid #eff0ff',
    borderRadius: 4,
    position: 'relative',

    '&:hover #placeholder': {
      opacity: (props) => (props.addable ? 0.5 : 1.0)
    },

    '&:hover #addWrapper': {
      opacity: (props) => (props.addable ? 1.0 : 0.0),
      border: `3px solid ${theme.palette.secondary.main}`,
      borderRadius: 4,
      clickEvents: (props) => (props.addable ? 'auto' : 'none')
    },

    '&:hover #addWrapper button': {
      opacity: (props) => (props.addable ? 1.0 : 0.0)
    }
  },

  addWrapper: {
    width: '100%',
    height: 'inherit',
    position: 'absolute',
    justifyContent: 'center',
    alignItems: 'center',
    display: (props) => (props.addable ? 'flex' : 'none'),
    top: 0,
    left: 0,
    pointerEvents: (props) => (props.addable ? 'auto' : 'none'),
    opacity: 0,
    zIndex: 100,
    '& .MuiSvgIcon-fontSizeLarge': {
      fontSize: '2.5rem'
    }
  }

}));


interface FileCardProps {
  fileName: string
  fileUrl?: string | null
  placeHolderType: PlaceholderType
  onClick?: () => void
  handleEditClick?: () => void
}

const FileCard: React.FC<FileCardProps> = ({ fileName, fileUrl, placeHolderType, onClick, handleEditClick }) => {
  const isLinkedin = fileName === 'Linkedin';
  const hideFileName = isLinkedin || placeHolderType === PlaceholderType.Video;
  const imgPath = isLinkedin ? '/linkedin.png' : '/file-download.svg';
  const backgroundColor = isLinkedin ? '#EFF6FF' : '#EFF0FF';
  const hasUrl = fileUrl && fileUrl.trim() !== '';
  const addable = !hasUrl && handleEditClick != null;
  const editable = !addable && handleEditClick != null;
  const styles = useFileCardStyles({ addable, editable });

  const handleClick = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    if (handleEditClick) {
      if (addable) {
        handleEditClick();
        return;
      }
    }

    if (onClick !== undefined) {
      onClick();
    } else if (fileUrl) {
      window.open(fileUrl, '__blank');
    }
  };

  return (
    <Box onClick={handleClick} className={styles.fileCard}>
      <StyledText color='textSecondary'>{fileName}</StyledText>
      <Box className={styles.fileCardContent} style={{ backgroundColor, borderRadius: 4 }}>
        {addable && (
          <div className={styles.addWrapper} id='addWrapper'>
            <AddCircleOutlineIcon color='secondary' fontSize='large' />
          </div>
        )}
        {!hasUrl && <Placeholder type={placeHolderType} />}
        {hasUrl && fileUrl && placeHolderType === PlaceholderType.Video && (
          <VideoPlayer
            url={fileUrl}
            onClick={handleClick}
            isSmall
          />
        )}
        {hasUrl && fileUrl && placeHolderType !== PlaceholderType.Video && (
          <img
            style={{ margin: hideFileName ? 'auto' : '15px' }}
            width={hideFileName ? 'auto' : '36.35px'}
            src={imgPath}
            alt={fileName}
          />
        )}
        {hasUrl && fileUrl && !hideFileName && (<Typography variant='h5' color='textPrimary'>{fileName}</Typography>)}
      </Box>
    </Box>
  );
};

const EntrepreneurPitchPanel: React.FC<EntrepreneurPitchPanelProps> = ({
  profile, handleEditItem
}) => {
  const {
    elevatorPitchUrl,
    fullPitchUrl,
    pitchDeckUrl,
    executiveSummaryUrl,
    linkedinProfileUrl
  } = profile;
  const [playingFullPitch, setPlayingFullPitch] = useState(false);

  const featuredVidUrl: string|null = getFeaturedVidUrl(elevatorPitchUrl, fullPitchUrl);
  const secondaryVidUrl: string|null = getSecondaryVidUrl(elevatorPitchUrl, fullPitchUrl);
  const hasAllRequiredItems:boolean = !!(pitchDeckUrl && executiveSummaryUrl && linkedinProfileUrl);

  const hideVideos = hasAllRequiredItems && !featuredVidUrl;
  const hideSecondaryVid = hideVideos || (featuredVidUrl && !secondaryVidUrl);

  const addable = !hideVideos && !featuredVidUrl && handleEditItem != null;
  const editable = !addable && handleEditItem != null;
  const styles = useStyles({ addable, editable });

  const handleFeaturedEditClick = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    if (!handleEditItem) return;

    if (editable) {
      // we may need to intercept the click  and stop propagation to prevent playing the video
      const { target } = event;
      if (target) {
        const { id } = (target as HTMLElement);
        if (id && (id === 'editIcon' || id === 'editButton' || id === 'editWrapper')) {
          event.stopPropagation();
        }
      }
    }

    let itemToEdit = PitchPanelItem.Elevator;
    if (fullPitchUrl && !elevatorPitchUrl) {
      itemToEdit = PitchPanelItem.FullPitch;
    }
    handleEditItem(itemToEdit);
  };


  const featuredComponent = () => (
    (featuredVidUrl || secondaryVidUrl)
      ? (
        <div className={styles.elevator}>
          {
          editable && (
            <div className={styles.editIconWrapper} id='editWrapper' onClick={handleFeaturedEditClick}>
              <IconButton size='medium' id='editButton'>
                <EditIcon color='secondary' id='editIcon' />
              </IconButton>
            </div>
          )
        }
          <VideoPlayer url={featuredVidUrl || secondaryVidUrl!} />
        </div>
      )
      : (
        <div className={styles.featuredPlaceholderContainer} onClick={handleFeaturedEditClick}>
          {addable && (
            <div className={styles.addWrapper} id='addWrapper'>
              <AddCircleOutlineIcon color='secondary' fontSize='large' />
            </div>
          )}
          <Placeholder type={PlaceholderType.Video} />
          <StyledText variant='h4' color='textSecondary' style={{ position: 'absolute', left: 20, bottom: 20 }}>Elevator Pitch</StyledText>
        </div>
      )
  );

  return (
    <Box className={styles.root} display='flex' flexDirection='column'>
      <VideoPlayerModal
        url={fullPitchUrl}
        modalProps={{ open: playingFullPitch, onClose: () => { setPlayingFullPitch(false); } }}
      />

      { hideVideos ? null : featuredComponent() }

      <Box className={styles.docs}>
        <FileCard
          fileName='Linkedin'
          fileUrl={linkedinProfileUrl}
          placeHolderType={PlaceholderType.Link}
          handleEditClick={handleEditItem && (() => { handleEditItem(PitchPanelItem.LinkedIn); })}
        />
        <FileCard
          fileName='Executive Summary'
          fileUrl={executiveSummaryUrl}
          placeHolderType={PlaceholderType.Files}
          handleEditClick={handleEditItem && (() => { handleEditItem(PitchPanelItem.ExecutiveSummary); })}
        />
        <FileCard
          fileName='Pitch Deck'
          fileUrl={pitchDeckUrl}
          placeHolderType={PlaceholderType.Files}
          handleEditClick={handleEditItem && (() => { handleEditItem(PitchPanelItem.PitchDeck); })}
        />
        {!hideSecondaryVid && (
          <FileCard
            fileName='Full-length Pitch'
            fileUrl={secondaryVidUrl}
            placeHolderType={PlaceholderType.Video}
            onClick={() => setPlayingFullPitch(true)}
            handleEditClick={handleEditItem && (() => { handleEditItem(PitchPanelItem.FullPitch); })}
          />
        )}
      </Box>
      <br />
      <Divider />
    </Box>
  );
};

export default EntrepreneurPitchPanel;
